import {inject, Injectable} from '@angular/core';
import {catchError, finalize, forkJoin, map, Observable, of, shareReplay, switchMap} from 'rxjs';
import {ProjectFixedCollectionData, ProjectFixedCollectionRequest} from '@frontend/shared-api';
import {DirectusSelect, DirectusService} from '@frontend/directus';
import {ProjectFixedCollectionService} from './project-fixed-collection.service';

@Injectable({
  providedIn: 'root',
})
export class FixedValueService {
  private readonly projectFixedCollectionService = inject(ProjectFixedCollectionService);
  private readonly directusService = inject(DirectusService);

  private readonly initializationCache = new Map<string, Observable<void>>();

  loadOrInitializeFixedDropdownValues(
    projectId: number,
    collectionName: string,
  ): Observable<DirectusSelect> {
    const currentValues$ = this.directusService.loadDropdownWithValues(collectionName);

    const storedIds$ = this.projectFixedCollectionService
      .getDirectusIdsForCollection(projectId, collectionName)
      .pipe(catchError(() => of([])));

    return storedIds$.pipe(
      switchMap((storedIds: string[]) =>
        currentValues$.pipe(
          map((dropdown) => {
            if (storedIds?.length > 0) {
              return {
                ...dropdown,
                values: dropdown.values.filter((value) => storedIds.includes(value.id)),
              };
            }
            return dropdown;
          }),
        ),
      ),
    );
  }

  loadOrInitializeMultipleFixedCollections(
    projectId: number,
    collectionNames: string[],
  ): Observable<void> {
    const cacheKey = `${projectId}-${collectionNames.toSorted((a, b) => a.localeCompare(b)).join(',')}`;

    const cachedRequest = this.initializationCache.get(cacheKey);
    if (cachedRequest) {
      return cachedRequest;
    }

    const tasks = collectionNames.map((name) =>
      this.loadOrInitializeFixedValues(projectId, name).pipe(catchError(() => of(null))),
    );

    const request$ = forkJoin(tasks).pipe(
      map(() => void 0),
      shareReplay(1),
      finalize(() => this.initializationCache.delete(cacheKey)),
    );

    this.initializationCache.set(cacheKey, request$);
    return request$;
  }

  private storeFixedValuesBatch(
    collectionConfigs: {
      collectionName: string;
      loadFunction?: () => Observable<{id: string}[]>;
    }[],
    projectId: number,
  ): Observable<{[key: string]: string[]}> {
    if (collectionConfigs.length === 0) {
      return of({});
    }

    const loadTasks = collectionConfigs.map((config) => {
      if (!config.loadFunction) {
        return of({
          name: config.collectionName,
          directusIds: [],
        });
      }

      return config.loadFunction().pipe(
        map((items) => ({
          name: config.collectionName,
          directusIds: items.map((item) => item.id),
        })),
      );
    });

    return forkJoin(loadTasks).pipe(
      switchMap((collections: ProjectFixedCollectionData[]) => {
        const nonEmptyCollections = collections.filter(
          (collection) => collection.directusIds.length > 0,
        );

        if (nonEmptyCollections.length === 0) {
          const emptyResults: {[key: string]: string[]} = {};
          collections.forEach((collection) => {
            emptyResults[collection.name] = [];
          });
          return of(emptyResults);
        }

        return this.projectFixedCollectionService
          .createMultiple(projectId, nonEmptyCollections)
          .pipe(
            map((batchResults) => {
              const finalResults: {[key: string]: string[]} = {};
              collections.forEach((collection) => {
                finalResults[collection.name] = batchResults[collection.name] || [];
              });
              return finalResults;
            }),
          );
      }),
    );
  }

  loadOrInitializeFixedValues(
    projectId: number,
    collectionName: string,
    loadFunction?: () => Observable<{id: string}[]>,
  ): Observable<string[]> {
    return this.projectFixedCollectionService
      .getDirectusIdsForCollection(projectId, collectionName)
      .pipe(
        switchMap((existingIds) => {
          const fixedValuesAlreadyStored = existingIds.length > 0;
          if (fixedValuesAlreadyStored) {
            return of(existingIds);
          }
          return this.storeFixedValues(loadFunction, collectionName, projectId);
        }),
      );
  }

  loadOrInitializeFixedValuesWithCollectionId(
    projectId: number,
    collectionName: string,
    loadFunction?: () => Observable<{id: string}[]>,
  ): Observable<{collectionId: number; directusIds: string[]}> {
    return this.projectFixedCollectionService
      .getValuesWithCollectionId(projectId, collectionName)
      .pipe(
        switchMap((response) => {
          const fixedValuesAlreadyStored = response.directusIds.length > 0;
          if (fixedValuesAlreadyStored) {
            return of(response);
          }
          // Need to store values first, then return with collection ID
          return this.storeFixedValues(loadFunction, collectionName, projectId).pipe(
            switchMap((_directusIds) =>
              this.projectFixedCollectionService
                .getValuesWithCollectionId(projectId, collectionName)
                .pipe(map((updatedResponse) => updatedResponse)),
            ),
          );
        }),
      );
  }

  loadOrInitializeFixedValuesBatch(
    projectId: number,
    collectionConfigs: {
      collectionName: string;
      loadFunction?: () => Observable<{id: string}[]>;
    }[],
  ): Observable<{[key: string]: string[]}> {
    if (collectionConfigs.length === 0) {
      return of({});
    }

    const collectionNames = collectionConfigs.map((config) => config.collectionName);

    return this.projectFixedCollectionService
      .getDirectusIdsForCollectionsBatch(projectId, collectionNames)
      .pipe(
        switchMap((batchResults) => {
          const missingCollections = collectionConfigs.filter((config) => {
            const existingIds = batchResults[config.collectionName] || [];
            return existingIds.length === 0;
          });

          if (missingCollections.length === 0) {
            return of(batchResults);
          }

          return this.storeFixedValuesBatch(missingCollections, projectId).pipe(
            map((newResults) => ({...batchResults, ...newResults})),
          );
        }),
      );
  }

  private storeFixedValues(
    loadFunction: (() => Observable<{id: string}[]>) | undefined,
    collectionName: string,
    projectId: number,
  ): Observable<string[]> {
    const loadObservable = loadFunction
      ? loadFunction().pipe(map((items) => items.map((item) => item.id)))
      : this.directusService
          .loadDropdownWithValues(collectionName)
          .pipe(map((dropdown) => dropdown.values.map((value) => value.id)));

    return loadObservable.pipe(
      switchMap((directusIds) => {
        if (directusIds.length === 0) {
          return of([]);
        }
        const dto: ProjectFixedCollectionRequest = {
          projectId,
          name: collectionName,
          directusIds,
        };
        return this.projectFixedCollectionService
          .create(dto)
          .pipe(map((response) => response?.directusIds || directusIds));
      }),
      catchError(() => {
        return of([]);
      }),
    );
  }
}
